import {
  getCancellationWarningParagraphs,
  PreCancelPlanDialogContent,
} from "@bounce/shared/consumer/pages/account-dashboard";
import NiceModal, {NiceModalHocProps} from "@ebay/nice-modal-react";
import React, {useCallback, useEffect, useMemo} from "react";
import {useGetAccount} from "~/api/requests";
import {Pages} from "~/bi-events/enum";
import {useWebchat} from "~/components/webchat/useWebchat";
import {useAmplitude, useConsumerUser} from "~/contexts";
import CancelPaymentPlanDialog from "./CancelPaymentPlanDialog";

interface IPreCancelPlanDialog extends NiceModalHocProps {
  accountId: string;
  shouldDisplayRewardsWarning: boolean;
}

const PreCancelPlanDialog = NiceModal.create(({accountId, shouldDisplayRewardsWarning}: IPreCancelPlanDialog) => {
  const {userContext} = useConsumerUser();
  const {pageViewEvent, pageClickEvent} = useAmplitude();
  const {openChat} = useWebchat();
  const {data: account} = useGetAccount({accountId}, {enabled: !!accountId});
  const activePaymentPlanId = account?.active_payment_plan?.payment_plan_id;
  const originalBalance = account?.bundle_balance_breakdown?.total_bundle_balance ?? account?.total_balance;

  const handleClickProceed = () => {
    pageClickEvent({page: Pages.CANCELLATION_INFO, cta: "talk_to_expert"});
    openChat({});
  };

  const openCancelPlanDialog = useCallback(async (): Promise<void> => {
    pageClickEvent({page: Pages.CANCELLATION_INFO, cta: "cancel_plan"});
    await NiceModal.show(CancelPaymentPlanDialog, {
      accountId: accountId,
      activePaymentPlanId,
      originalBalance,
      shouldDisplayRewardsWarning,
    });
  }, [accountId, originalBalance, shouldDisplayRewardsWarning]);

  useEffect(() => {
    pageViewEvent({page: Pages.CANCELLATION_INFO});
  }, []);

  const {first: firstParagraph, second: secondParagraph} = useMemo(
    () => getCancellationWarningParagraphs(originalBalance),
    [originalBalance]
  );

  return (
    <PreCancelPlanDialogContent
      username={userContext?.user_name}
      buttonText="Speak to an expert"
      firstParagraph={firstParagraph}
      secondParagraph={secondParagraph}
      handleClickProceed={handleClickProceed}
      openCancelPlanDialog={openCancelPlanDialog}
    />
  );
});

export default PreCancelPlanDialog;
